import { client, getNoaWorkLog } from '../../services/api';
import type { EntityWorkLogDialogProps } from './EntityWorkLogDialog';
import type { WorkLogPage } from './types';

/**
 * Display names for the NOA columns that show up in the work log. Anything not
 * listed falls back to the humanized column name.
 */
export const NOA_WORK_LOG_FIELD_LABELS: Record<string, string> = {
  status: 'Status',
  physician_npi: 'Physician NPI',
  physician_first_name: 'Physician first name',
  physician_last_name: 'Physician last name',
  dcn_icn_number: 'DCN/ICN',
  claim_number: 'Claim number',
  patient_control_number: 'Patient control number',
  kx_modifier: 'KX modifier',
  kx_modifier_remarks: 'KX modifier remarks',
  sent_date: 'Sent date',
  notes: 'Notes',
  accepted_externally: 'Accepted externally',
  condition_codes: 'Condition codes',
  occurrence_codes: 'Occurrence codes',
  occurrence_spans: 'Occurrence spans',
  value_codes: 'Value codes',
  remarks: 'Remarks',
};

/** The NOA status codes a log entry can move between, as the NOA screens name them. */
export const NOA_STATUS_LABELS: Record<string, string> = {
  not_ready: 'Not Yet Ready',
  ready_to_send: 'Ready to Send',
  sent: 'Sent',
  acknowledged: 'Acknowledged',
  accepted: 'Accepted',
  rejected: 'Rejected',
  paid: 'Paid',
  not_applicable: 'Not Applicable',
};

const formatNoaWorkLogValue = (
  field: string,
  value: unknown
): string | undefined =>
  field === 'status' && typeof value === 'string'
    ? NOA_STATUS_LABELS[value]
    : undefined;

/**
 * Everything the NOA work log dialog shows apart from which NOA it loads.
 * NOAForm and the NOA Manager row both spread this, so the two ways into the
 * log cannot drift apart.
 */
export const NOA_WORK_LOG_DIALOG_PROPS = {
  title: 'Work log',
  description: 'Edits to this Notice of Admission, oldest first.',
  fieldLabels: NOA_WORK_LOG_FIELD_LABELS,
  formatValue: formatNoaWorkLogValue,
  // The day every NOA write path started writing audit rows.
  trackedSince: '2026-09-28',
} satisfies Partial<EntityWorkLogDialogProps>;

/**
 * The one fetch behind every NOA work-log dialog (editor popup, NOA Manager row).
 *
 * The NOA log is read whole: its endpoint takes no cursor. So the page it
 * returns never offers a next one, and the dialog cannot ask for page one
 * again in a loop if the endpoint ever starts sending a cursor.
 */
export async function loadNoaWorkLog(noaId: string): Promise<WorkLogPage> {
  const res = await getNoaWorkLog({
    path: { noa_id: noaId },
    client,
    // Resolves on a 4xx/5xx otherwise, and the dialog would read an error
    // as an empty log.
    throwOnError: true,
  });
  if (!res.data) throw new Error('No work log in response');
  return { work_log: res.data.work_log, next_cursor: null };
}
